import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { useNavigate } from 'react-router-dom';
import { Course, Program } from '../lectures/user-page-lecturer-lectures';
import globalUserPage from '../../../../../stores/global-user-page';
import dateUtils from '../../../../../utils/date-format';
import { AssignmentsLecturer } from '../../../../../api/types/lecturers/assignments/assignments-lecturer';

interface Assignment {
  id: number;
  name: string;
  dueDate: Date;
  course: Course;
  program: Program;
}

// Mock Data
const mockAssignments: Assignment[] = [
  {
    id: 1,
    name: 'תרגיל 1 - מבוא לתכנות',
    dueDate: new Date('2024-03-20'),
    course: {
      id: 1,
      name: 'מבוא למדעי המחשב',
    },
    program: {
      id: 1,
      name: 'Computer Science',
      startYear: 2024,
      programNumberInYear: 1,
    },
  },
  {
    id: 2,
    name: 'תרגיל 2 - מערכי נתונים',
    dueDate: new Date('2024-03-25'),
    course: {
      id: 2,
      name: 'מבני נתונים',
    },
    program: {
      id: 1,
      name: 'Computer Science',
      startYear: 2024,
      programNumberInYear: 1,
    },
  },
  {
    id: 3,
    name: 'תרגיל 1 - מיון וחיפוש',
    dueDate: new Date('2024-03-30'),
    course: {
      id: 3,
      name: 'אלגוריתמים',
    },
    program: {
      id: 1,
      name: 'Computer Science',
      startYear: 2024,
      programNumberInYear: 1,
    },
  },
];

export const UserPageLecturerAssignments = () => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<AssignmentsLecturer | null>(null);
  const navigate = useNavigate();

  /*  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setAssignments(mockAssignments);
      setLoading(false);
    }, 1000);
  }, []);*/

  const handleGradeInputClick = (assignment: AssignmentsLecturer) => {
    setSelectedAssignment(assignment);
    setShowDialog(true);
  };

  const handleNavigateToCourse = async () => {
    if (selectedAssignment) {
      const { branchId, programId, courseId } = selectedAssignment;

      try {
        // Navigate to course page
        const path = `/campuses/${branchId}/programs/${programId}/courses/${courseId}`;
        navigate(path);
      } catch (error) {
        console.error('Error loading course details:', error);
        // Still try to navigate even if loading fails
        navigate(
          `/campuses/${branchId}/programs/${programId}/courses/${courseId}`,
        );
      }
    }
    setShowDialog(false);
    setSelectedAssignment(null);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedAssignment(null);
  };

  if (loading) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ height: '100vh' }}
      >
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-4" style={{ width: '100%', maxWidth: '100%' }}>
      <div className="grid">
        <div className="col-12">
          <h2 className="mb-3">מטלות</h2>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <DataTable
              value={globalUserPage.lecturerDetails?.assignments || []}
              rows={10}
              emptyMessage="אין מטלות"
              className="p-datatable-striped"
              style={{ width: '100%', minWidth: '100%' }}
              scrollable
              scrollHeight="flex"
              resizableColumns
              columnResizeMode="expand"
            >
              <Column
                field="programName"
                header="שם התוכנית"
                style={{ width: '15%', textAlign: 'right' }}
              />
              <Column
                field="programStartYear"
                header="שנת התחלה"
                style={{ width: '10%', textAlign: 'right' }}
              />
              <Column
                field="annualProgramNumber"
                header="מספר תוכנית בשנה"
                style={{ width: '15%', textAlign: 'right' }}
              />
              <Column
                field="courseName"
                header="קורס"
                style={{ width: '20%', textAlign: 'right' }}
              />
              <Column
                field="assignmentName"
                header="שם המטלה"
                style={{ width: '20%', textAlign: 'right' }}
              />
              <Column
                field="dueDate"
                header="תאריך הגשה"
                style={{ width: '10%', textAlign: 'right' }}
                body={(rowData: AssignmentsLecturer) => (
                  <div>
                    {dateUtils.displayFormatDate(rowData.assignmentDueDate)}
                  </div>
                )}
              />
              <Column
                header="פעולות"
                style={{ width: '10%', textAlign: 'right' }}
                body={(rowData: AssignmentsLecturer) => (
                  <Button
                    label="הזן ציונים"
                    icon="pi pi-pencil"
                    className="p-button-sm p-button-outlined"
                    onClick={() => handleGradeInputClick(rowData)}
                  />
                )}
              />
            </DataTable>
          </div>
        </div>
      </div>

      {/* Grade Input Dialog */}
      <Dialog
        header="הזנת ציונים"
        visible={showDialog}
        style={{
          width: '500px',
          maxWidth: '90vw',
          borderRadius: '8px',
        }}
        modal
        onHide={handleCloseDialog}
        resizable={false}
        draggable={false}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p
            style={{
              fontSize: '16px',
              marginBottom: '20px',
              lineHeight: '1.5',
            }}
          >
            על מנת להזין ציונים יש לעבור לדף הקורס{' '}
            {selectedAssignment && (
              <strong>{selectedAssignment.courseName}</strong>
            )}
          </p>
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
          >
            <Button
              label="מעבר לדף הקורס"
              icon="pi pi-external-link"
              onClick={handleNavigateToCourse}
              style={{ minWidth: '150px' }}
            />
            <Button
              label="ביטול"
              icon="pi pi-times"
              outlined
              onClick={handleCloseDialog}
              style={{ minWidth: '150px' }}
            />
          </div>
        </div>
      </Dialog>

      <style>
        {`
          .p-datatable .p-datatable-thead > tr > th {
            background-color: var(--surface-section);
            color: var(--surface-900);
            font-weight: 600;
          }
          .p-datatable .p-datatable-tbody > tr {
            background-color: var(--surface-ground);
          }
          .p-datatable .p-datatable-tbody > tr:nth-child(even) {
            background-color: var(--surface-hover);
          }
          .p-datatable-wrapper {
            margin-right: 0 !important;
            margin-left: 0 !important;
          }
        `}
      </style>
    </div>
  );
};
